/**
 * Widget App Component
 *
 * Main React component for the LongChat widget.
 * Manages the chat state and coordinates between components.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FloatingButton } from './components/FloatingButton';
import { ChatWindow } from './components/ChatWindow';
import { initializeFirebase, signInAnonymouslyToFirebase } from './lib/firebase';
import {
  openOrResumeChat,
  createChat,
  closeChat,
  sendMessage,
  listenToMessages,
  listenToChat,
  WidgetRateLimitError,
} from './lib/chatApi';
import { getAgentInfo, getAccountAllowedOrigins } from './lib/agentApi';
import { getVisitorId } from './lib/visitorId';
import {
  getLabels,
  type WidgetMessage,
  type LongChatInitOptions,
  type Labels,
  type AgentInfo,
} from './types/widget';

interface WidgetAppProps {
  options: LongChatInitOptions;
  onSendMessageRequest?: (text: string) => Promise<void>;
}

export function WidgetApp({ options, onSendMessageRequest }: WidgetAppProps) {
  const [isOriginAllowed, setIsOriginAllowed] = useState<boolean | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const [chatMode, setChatMode] = useState<'bot' | 'agent'>('bot');
  const [messages, setMessages] = useState<WidgetMessage[]>([]);
  const [visitorId, setVisitorId] = useState<string>('');
  const [agentInfo, setAgentInfo] = useState<AgentInfo | null>(null);
  const [isBotTyping, setIsBotTyping] = useState(false);

  // Expand/collapse: initialized from static config, then fully user-controlled.
  const [isExpanded, setIsExpanded] = useState<boolean>(Boolean(options.fullscreen));

  // Local-only "clear from screen" state (never touches the server).
  const [hiddenMessageIds, setHiddenMessageIds] = useState<Set<string>>(() => new Set());
  const [clearScreenAtMs, setClearScreenAtMs] = useState<number | null>(null);

  // Guards the chat listener so an intentional new-chat switch does not trigger
  // the "closed by agent" reset path.
  const isStartingNewChatRef = useRef(false);

  const [blockedUntilTs, setBlockedUntilTs] = useState<number | null>(null);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [nowTs, setNowTs] = useState<number>(Date.now());

  const labels: Labels = getLabels(options.lang || 'he');
  const position = options.theme?.position || 'bottom-right';
  const primaryColor = options.theme?.primaryColor || '#FF3E6C';
  const buttonSize = clampNumber(options.theme?.buttonSize, 40, 96, 56);
  const offsetX = clampNumber(options.theme?.offsetX, 0, 200, 24);
  const offsetY = clampNumber(options.theme?.offsetY, 0, 200, 24);

  useEffect(() => {
    async function verifyOrigin() {
      try {
        initializeFirebase(options.firebaseConfig);

        const allowedOrigins = await getAccountAllowedOrigins(options.accountId);
        const currentOrigin = window.location.origin;

        if (allowedOrigins.includes(currentOrigin)) {
          setIsOriginAllowed(true);
        } else {
          console.warn(
              `[LongChat Security] Widget blocked. Unauthorized origin: ${currentOrigin}. Please add it to your allowed domains in the dashboard.`
          );
          setIsOriginAllowed(false);
        }
      } catch (err) {
        console.error('[LongChat] Failed to verify origin security:', err);
        setIsOriginAllowed(false);
      }
    }

    verifyOrigin();
  }, [options.accountId, options.firebaseConfig]);

  useEffect(() => {
    if (!isOpen || isInitialized || isOriginAllowed !== true) return;

    async function initializeChat() {
      try {
        setIsLoading(true);
        setError(null);

        await signInAnonymouslyToFirebase();

        const vid = options.user?.externalId || getVisitorId();
        setVisitorId(vid);

        const agent = await getAgentInfo(options.accountId, options.agentId);
        setAgentInfo(agent);

        const chat = await openOrResumeChat(options.accountId, options.agentId, vid, {
          userFirstName: options.user?.firstName,
          userContactInformation: options.user?.contactInformation,
          initialContext: {
            ...(options.context || {}),
            userContext: options.context?.userContext,
          },
        });

        setChatId(chat.id);
        setIsInitialized(true);
        console.log('[LongChat] Widget initialized, chat:', chat.id);
      } catch (err) {
        console.error('[LongChat] Initialization error:', err);
        setError('Failed to initialize chat');
      } finally {
        setIsLoading(false);
      }
    }

    initializeChat();
  }, [isOpen, isInitialized, isOriginAllowed, options]);

  useEffect(() => {
    if (!chatId || !isInitialized) return;

    const unsubscribe = listenToChat(
        options.accountId,
        options.agentId,
        chatId,
        (chat) => {
          setChatMode(chat.mode);

          const blockedUntilFromChat =
              chat.rateLimitState?.blockedUntil?.toMillis?.() ?? null;

          if (chat.mode === 'agent') {
            setIsBotTyping(false);
            setBlockedUntilTs(null);
            setBlockedMessage(null);
          } else {
            if (blockedUntilFromChat && blockedUntilFromChat > Date.now()) {
              setBlockedUntilTs(blockedUntilFromChat);
              setBlockedMessage('הגעת למגבלת ההודעות בזמן המסוים.');
            } else {
              setBlockedUntilTs(null);
              setBlockedMessage(null);
            }
          }

          if (chat.status === 'closed') {
            // Ignore the close that we triggered ourselves while starting a new chat.
            if (isStartingNewChatRef.current) {
              return;
            }

            console.log('[LongChat] Chat closed by agent, resetting widget state');
            unsubscribe();
            setMessages([]);
            setHiddenMessageIds(new Set());
            setClearScreenAtMs(null);
            setChatId(null);
            setIsInitialized(false);
            setIsBotTyping(false);
            setChatMode('bot');
            setBlockedUntilTs(null);
            setBlockedMessage(null);
            setIsOpen(false);
          }
        },
        (err) => {
          console.error('[LongChat] Chat listener error:', err);
        }
    );

    return () => unsubscribe();
  }, [chatId, isInitialized, options.accountId, options.agentId]);

  useEffect(() => {
    if (!chatId || !isInitialized) return;

    const unsubscribe = listenToMessages(
        options.accountId,
        options.agentId,
        chatId,
        (newMessages) => {
          const resolved = newMessages.map((m) => {
            if (m.senderType === 'bot' && agentInfo?.name) {
              return { ...m, senderName: agentInfo.name };
            }
            return m;
          });

          setMessages(resolved);

          const last = resolved[resolved.length - 1];
          if (last && last.senderType === 'bot') {
            setIsBotTyping(false);
          }
        },
        (err) => {
          console.error('[LongChat] Message listener error:', err);
          setError('Failed to load messages');
          setIsBotTyping(false);
        }
    );

    return () => {
      unsubscribe();
    };
  }, [chatId, isInitialized, agentInfo, options.accountId, options.agentId]);

  useEffect(() => {
    if (!blockedUntilTs) return;

    const interval = window.setInterval(() => {
      setNowTs(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, [blockedUntilTs]);

  useEffect(() => {
    if (!blockedUntilTs) return;

    if (Date.now() >= blockedUntilTs) {
      setBlockedUntilTs(null);
      setBlockedMessage(null);
    }
  }, [nowTs, blockedUntilTs]);

  const remainingBlockedSeconds = useMemo(() => {
    if (!blockedUntilTs) return 0;
    return Math.max(0, Math.ceil((blockedUntilTs - nowTs) / 1000));
  }, [blockedUntilTs, nowTs]);

  const isBlocked = chatMode === 'bot' && remainingBlockedSeconds > 0;

  const handleSendMessage = useCallback(
      async (text: string) => {
        if (!chatId || !text.trim()) return;

        if (chatMode === 'bot' && blockedUntilTs && Date.now() < blockedUntilTs) {
          return;
        }

        setError(null);

        const tempMessage: WidgetMessage = {
          id: `temp-${Date.now()}`,
          senderType: 'endUser',
          content: text,
          timestamp: new Date(),
          status: 'pending',
        };

        setMessages((prev) => [...prev, tempMessage]);
        setIsSending(true);

        if (chatMode === 'bot') {
          setIsBotTyping(true);
        }

        try {
          if (onSendMessageRequest) {
            await onSendMessageRequest(text);
          } else {
            await sendMessage(
                options.accountId,
                options.agentId,
                chatId,
                visitorId,
                text,
                {
                  url: window.location.href,
                  pageTitle: document.title,
                  currentPage: window.location.href,
                }
            );
          }
        } catch (err) {
          console.error('[LongChat] Send error:', err);

          if (err instanceof WidgetRateLimitError) {
            setMessages((prev) => prev.filter((msg) => msg.id !== tempMessage.id));

            const blockedUntil =
                err.blockedUntilTs ?? Date.now() + err.retryAfterSeconds * 1000;

            setBlockedUntilTs(blockedUntil);
            setBlockedMessage(err.message);
          } else {
            setMessages((prev) =>
                prev.map((msg) =>
                    msg.id === tempMessage.id ? { ...msg, status: 'failed' as const } : msg
                )
            );
            setError('שליחת ההודעה נכשלה, נסה שוב.');
          }

          setIsBotTyping(false);
        } finally {
          setIsSending(false);
        }
      },
      [
        chatId,
        chatMode,
        blockedUntilTs,
        options.accountId,
        options.agentId,
        onSendMessageRequest,
        visitorId,
      ]
  );

  // Messages actually shown: real messages minus locally cleared ones.
  // Hidden either by id (snapshot taken at clear time) or by being created at/
  // before the clear timestamp (covers temp ids that later become Firestore ids).
  const visibleMessages = useMemo(() => {
    if (!hiddenMessageIds.size && clearScreenAtMs == null) {
      return messages;
    }
    return messages.filter((m) => {
      if (hiddenMessageIds.has(m.id)) return false;
      if (clearScreenAtMs != null) {
        const t =
            m.timestamp instanceof Date ? m.timestamp.getTime() : 0;
        if (t > 0 && t <= clearScreenAtMs) return false;
      }
      return true;
    });
  }, [messages, hiddenMessageIds, clearScreenAtMs]);

  const toggleChat = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!next) {
        // Reset transient expand state when closing so it re-derives from config.
        setIsExpanded(Boolean(options.fullscreen));
      }
      return next;
    });
  };

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Clear chat from screen — local only, never deletes anything on the server.
  const handleClearMessages = useCallback(() => {
    setClearScreenAtMs(Date.now());
    setHiddenMessageIds((prev) => {
      const next = new Set(prev);
      messages.forEach((m) => next.add(m.id));
      return next;
    });
  }, [messages]);

  // Start a new chat: close the old chat (status -> 'closed') then create a fresh one,
  // so the next resume cannot pick up the old conversation.
  const handleNewChat = useCallback(async () => {
    if (!visitorId || isStartingNewChatRef.current) return;

    isStartingNewChatRef.current = true;
    const oldChatId = chatId;

    try {
      setIsLoading(true);
      setError(null);

      // Reset local view state.
      setMessages([]);
      setHiddenMessageIds(new Set());
      setClearScreenAtMs(null);
      setIsBotTyping(false);
      setBlockedUntilTs(null);
      setBlockedMessage(null);
      setChatMode('bot');

      const newChat = await createChat(options.accountId, options.agentId, visitorId, {
        userFirstName: options.user?.firstName,
        userContactInformation: options.user?.contactInformation,
        initialContext: {
          ...(options.context || {}),
          userContext: options.context?.userContext,
        },
      });

      setChatId(newChat.id);
      setIsInitialized(true);

      // Close the previous chat after switching, so its listener (already being
      // torn down) plus the guard prevent the "closed" reset path from firing.
      if (oldChatId && oldChatId !== newChat.id) {
        try {
          await closeChat(options.accountId, options.agentId, oldChatId);
        } catch (closeErr) {
          console.warn('[LongChat] Could not close previous chat:', closeErr);
        }
      }
    } catch (err) {
      console.error('[LongChat] Failed to start new chat:', err);
      setError('Failed to start new chat');
    } finally {
      setIsLoading(false);
      isStartingNewChatRef.current = false;
    }
  }, [chatId, visitorId, options]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--longchat-primary', primaryColor);
    const hoverColor = adjustColor(primaryColor, -15);
    root.style.setProperty('--longchat-primary-hover', hoverColor);
  }, [primaryColor]);

  if (isOriginAllowed !== true) {
    return null;
  }

  return (
      <div className="longchat-widget" dir="rtl">
        <AnimatePresence>
          {isOpen && isExpanded && (
              <motion.div
                  key="lc-backdrop"
                  className="lc-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={handleToggleExpand}
              />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isOpen && (
              <ChatWindow
                  messages={visibleMessages}
                  isLoading={isLoading && !isInitialized}
                  error={error}
                  onSendMessage={handleSendMessage}
                  onClose={toggleChat}
                  position={position}
                  buttonSize={buttonSize}
                  offsetX={offsetX}
                  offsetY={offsetY}
                  labels={labels}
                  isSending={isSending}
                  agentInfo={agentInfo}
                  primaryColor={primaryColor}
                  isBotTyping={isBotTyping}
                  chatMode={chatMode}
                  isExpanded={isExpanded}
                  onToggleExpand={handleToggleExpand}
                  onNewChat={handleNewChat}
                  onClearMessages={handleClearMessages}
                  isBlocked={isBlocked}
                  blockedMessage={blockedMessage}
                  remainingBlockedSeconds={remainingBlockedSeconds}
              />
          )}
        </AnimatePresence>

        <FloatingButton
            onClick={toggleChat}
            isOpen={isOpen}
            position={position}
            buttonSize={buttonSize}
            offsetX={offsetX}
            offsetY={offsetY}
            tooltip={labels.fabTooltip}
            primaryColor={primaryColor}
        />
      </div>
  );
}


function clampNumber(
    value: number | undefined,
    min: number,
    max: number,
    fallback: number
): number {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * Adjust a hex color's brightness
 */
function adjustColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000ff) + amt));
  return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
}